import React, { useEffect, useRef, useState } from 'react'
import styles from '../DashboardPage.module.css'
import { useLayer } from '../../components/layers'
import { dowOf, forecastUrl, geocodeUrl, parseForecast, parsePlaces, wxMeta, WX_PATHS, type Forecast, type WxIcon, type WxLocation } from './model'

// Weather (§7; V4 wx*). Open-Meteo, no key. The location is the viewer's
// choice, saved against them per workspace; nothing seeds it yet (sites hold
// no coordinates — DASH-11), so an unset card asks for one. A reading is kept
// ~15 minutes per location, and a failed read shows the last one it had or a
// plain line, never a broken card (§7).

const TTL_MS = 15 * 60 * 1000
const cache = new Map<string, { at: number; data: Forecast }>()
const keyOf = (l: WxLocation) => `${l.lat},${l.lon}`

export function clearWeatherCache() {
  cache.clear()
}

export function WxGlyph({ icon, size = 22 }: { icon: WxIcon; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icon === 'sun' && <circle cx="12" cy="12" r="4" />}
      {WX_PATHS[icon].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}

interface Props {
  location: WxLocation | null
  onLocation: (l: WxLocation | null) => void
}

export function WeatherCard({ location, onLocation }: Props) {
  const [data, setData] = useState<Forecast | null>(() => (location ? cache.get(keyOf(location))?.data || null : null))
  const [failed, setFailed] = useState(false)
  const [open, setOpen] = useState(false)
  // The location picker is a layer (h 425-458): Escape closes it. A half-typed
  // search is not a change to anything, so it goes without asking.
  useLayer(open, () => setOpen(false))

  useEffect(() => {
    if (!location) return
    const k = keyOf(location)
    const hit = cache.get(k)
    setData(hit?.data || null)
    setFailed(false)
    if (hit && Date.now() - hit.at < TTL_MS) return
    let live = true
    fetch(forecastUrl(location))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => {
        const f = parseForecast(j)
        if (!f) throw new Error('unreadable forecast')
        cache.set(k, { at: Date.now(), data: f })
        if (live) setData(f)
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [location?.lat, location?.lon])

  const meta = data ? wxMeta(data.code) : null
  return (
    <div className={`${styles.card} ${styles.short}`} data-testid="widget-weather">
      <div className={styles.cardHead}>
        <span className={styles.cardTitle}>Weather</span>
        <div className={styles.wxPillWrap}>
          <button type="button" className={location ? styles.wxPill : styles.wxPillSet} onClick={() => setOpen((o) => !o)} title={location ? 'Change location' : undefined} aria-expanded={open}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 22s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12Z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            {location ? location.name : 'Set location'}
            {location && <span aria-hidden="true">▾</span>}
          </button>
          {open && (
            <LocationPicker
              onPick={(l) => {
                setOpen(false)
                onLocation(l)
              }}
            />
          )}
        </div>
      </div>
      {!location ? (
        <div className={styles.wxUnset}>
          <span className={styles.wxLabel}>No location set</span>
          <span className={styles.wxDetail}>Set your site's location to see current conditions and a four-day forecast. It saves for next time.</span>
        </div>
      ) : !data ? (
        <div className={styles.wxUnset}>
          <span className={styles.wxLabel}>{failed ? 'Weather is unavailable right now' : 'Loading…'}</span>
          {failed && <span className={styles.wxDetail}>The forecast service did not answer. It will try again next time the page loads.</span>}
        </div>
      ) : (
        <>
          <div className={styles.wxNow}>
            <span className={styles.wxWell}>
              <WxGlyph icon={meta!.icon} />
            </span>
            <span className={styles.wxTemp}>{data.temp}°</span>
            <div>
              <div className={styles.wxLabel}>{meta!.label}</div>
              <div className={styles.wxDetail}>
                {data.hi}° / {data.lo}° · wind {data.wind} km/h
              </div>
            </div>
          </div>
          {failed && <div className={styles.wxDetail}>Showing the last reading; the forecast service did not answer.</div>}
          <div className={styles.wxDays}>
            {data.days.map((d) => (
              <div key={d.date} className={styles.wxDay} title={wxMeta(d.code).label}>
                <span>{dowOf(d.date)}</span>
                <WxGlyph icon={wxMeta(d.code).icon} size={18} />
                <b>{d.hi}°</b>
                <span>{d.lo}°</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function LocationPicker({ onPick }: { onPick: (l: WxLocation) => void }) {
  const [q, setQ] = useState('')
  const [places, setPlaces] = useState<(WxLocation & { meta: string })[]>([])
  const [note, setNote] = useState<string | null>(null)
  const seq = useRef(0)

  useEffect(() => {
    const term = q.trim()
    const my = ++seq.current
    if (term.length < 2) {
      setPlaces([])
      setNote(null)
      return
    }
    const t = setTimeout(() => {
      fetch(geocodeUrl(term))
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((j) => {
          if (my !== seq.current) return
          const p = parsePlaces(j)
          setPlaces(p)
          setNote(p.length ? null : 'No places match that.')
        })
        .catch(() => {
          if (my !== seq.current) return
          setPlaces([])
          setNote('Search is unavailable right now.')
        })
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  const mine = () => {
    const geo = typeof navigator !== 'undefined' ? navigator.geolocation : undefined
    if (!geo) return setNote('This browser will not share a location.')
    setNote('Asking your browser…')
    geo.getCurrentPosition(
      (pos) => {
        const lat = Math.round(pos.coords.latitude * 100) / 100
        const lon = Math.round(pos.coords.longitude * 100) / 100
        // Named by its coordinates: no reverse lookup is in the design.
        onPick({ name: `${lat}, ${lon}`, lat, lon })
      },
      () => setNote('Location permission was declined. Search instead.'),
      { timeout: 10000 }
    )
  }

  return (
    <div className={styles.wxPop} role="dialog" aria-label="Weather location">
      <label className={styles.wxSearch}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input className={styles.wxInput} autoFocus placeholder="Search a town or city" aria-label="Search a town or city" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <button type="button" className={styles.wxMine} onClick={mine}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 2v3m0 14v3M2 12h3m14 0h3" />
          <circle cx="12" cy="12" r="6" />
        </svg>
        Use my location
      </button>
      {note && <div className={styles.wxNote}>{note}</div>}
      {places.map((p) => (
        <button key={`${p.lat},${p.lon},${p.name}`} type="button" className={styles.wxPlace} onClick={() => onPick({ name: p.name, lat: p.lat, lon: p.lon })}>
          {p.name}
          {p.meta && <small>{p.meta}</small>}
        </button>
      ))}
    </div>
  )
}
